/**
* ユーザー管理関連
*/

import axios from '@/plugins/axios';
import cf from '@/mixins/commonFunctions';
import moment from 'moment';
import login from './login';
import helperStore from '../helper';

const projectName = process.env.VUE_APP_PROJECTNAME;

const actions = {
  setUserData({ commit, dispatch }, data) {
    const user = data;

    // order並べ替え
    const userOrders = user.driverOrders || [];
    if (userOrders) {
      userOrders.sort((a, b) => {
        const aDatetime = moment(a.reservation.datetime || a.created_at).format('YYYY-MM-DD HH:mm:ss');
        const bDatetime = moment(b.reservation.datetime || b.created_at).format('YYYY-MM-DD HH:mm:ss');
        if (aDatetime > bDatetime) return 1;
        return -1;
      });
    }
    user.driverOrders = userOrders;

    commit('setUserData', user);
    dispatch('setOrders', user);
    dispatch('dispatch/getData', null, { root: true });
    if (user.offices?.[0]?.id) dispatch('helper/getMessageTemplate', null, { root: true });
  },

  /** DBからユーザー情報を取得しstate更新 */
  update({ commit, dispatch, state }, data) {
    const ls = cf.getLocalStorage(projectName);
    // localStorageが存在しない場合はスルー
    if (!ls) return;

    if (state.login.isLogin && ls.auth?.token) {
      axios({
        method: 'GET',
        url: '/v1/user/get/detail',
        params: {
          id: state.id,
          withVehicles: 1,
          withLatestPositions: 0,
          withOrders: 1,
          withRequests: 1,
          withDemandSchedules: 1,
          withTodaysSchedules: 1,
          withDriverDailyCheck: 2,
          withDriverDemandAreas: 1,
        },
      })
        .then((response) => {
          const user = response.data.detail;

          // order並べ替え
          const userOrders = user.driverOrders;
          userOrders.sort((a, b) => {
            const aDatetime = moment(a.reservation.datetime || a.created_at).format('YYYY-MM-DD HH:mm:ss');
            const bDatetime = moment(b.reservation.datetime || b.created_at).format('YYYY-MM-DD HH:mm:ss');
            if (aDatetime > bDatetime) return 1;
            return -1;
          });
          user.driverOrders = userOrders;

          commit('update', user);
          dispatch('dispatch/updateScheduleItems', null, { root: true });
          dispatch('setDriverStatus', user); // driverの状態をセットする
          dispatch('setOrders', user);
          if (data?.hideLoading) dispatch('modal/loadings/hideModal', null, { root: true });
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
        });
    }
  },
  setDriverStatus({ commit, rootState }, user) {
    const items = (rootState.dispatch.scheduleItems || [])
      .filter((obj) => {
        const isCompleted = (item) => {
          const COMPLETED_FLAG = 31;
          if (item.type === 'reservation') {
            if (item.raw.foreign_type === 31) return item.raw.demandCustomers.every((dc) => dc.flag >= COMPLETED_FLAG);
            return item.raw.order?.flag >= 31;
          }
          if (item.type === 'dispatch') return item.raw.passengers.every((p) => p.flag >= COMPLETED_FLAG);
          return true;
        };
        return !isCompleted(obj);
      });
    items.sort((a, b) => {
      const aDatetime = new Date(a.start);
      const bDatetime = new Date(b.start);
      if (aDatetime > bDatetime) return 1;
      return -1;
    });
    const recentOrder = items[0] || null;
    const rsv = recentOrder?.raw;
    const ordersMaster = helperStore.state.master?.orders;
    const activityMaster = helperStore.state.master?.driverActivities;

    let stateObj = {};
    let flag = 1;
    stateObj = ordersMaster?.flags.find((f) => f.name === 'empty'); // 初期値は空車
    const activeWds = (user.waitingDriver || []).filter((wd) => !wd.end_at);
    const displayTypePriority = [4, 2, 3, 1];
    const displayType = displayTypePriority.find((type) => activeWds.some((wd) => wd.type === type));
    if (displayType) {
      stateObj = activityMaster.items.find((s) => s.value === displayType);
    } else if (ordersMaster && recentOrder) {
      if (recentOrder.type === 'reservation') {
        if (rsv.foreign_type === 31) {
          const flags = rsv.demandCustomers.map((dc) => dc.flag).filter((f) => f < 900);
          if (flags.includes(21)) flag = 21;
          else if (flags.includes(11)) flag = 11;
          else if (flags.includes(2)) flag = 2;
          else if (flags.includes(1)) flag = 1;
        } else {
          flag = rsv.order.flag >= 31 ? 1 : rsv.order.flag;
        }
      }
      stateObj = ordersMaster?.flags.find((f) => flag === f.value);
    }

    const args = {
      orderId: recentOrder?.id || null,
      orderFlag: flag || null,
      ...stateObj,
      isBreak: activeWds.some((wd) => wd.type === 2),
      isWaiting: activeWds.some((wd) => wd.type === 1),
      isClosed: activeWds.some((wd) => wd.type === 3),
      isEmergency: activeWds.some((wd) => wd.type === 4),
    };
    commit('setDriverStatus', args);
  },

  // user.flagを更新するアクション
  async updateFlag({ dispatch, commit, state }, newFlag) {
    const ls = cf.getLocalStorage(projectName);
    // localStorageが存在しない場合はスルー
    if (!ls) return;

    if (state.login.isLogin && ls.auth?.token) {
      try {
        if (state.driverStatus.isWaiting) await dispatch('user/login/releaseWaitingDriver', null, { root: true });
        const response = await axios({
          method: 'POST',
          url: '/v1/user/update',
          data: {
            id: state.id,
            flag: newFlag,
            route: 'driver',
          },
        });
        if (response.status === 200) {
          commit('setFlag', newFlag);
        } else {
          throw new Error('Failed to update flag');
        }
      } catch (error) {
        console.error('Failed to update flag:', error);
        alert('処理中にエラーが発生しました。再度お試しください。');
      }
    }
  },
  setOrders({ commit }, user) {
    // [デマンド予約] demandSchedules.reservationsをreservationsの配列に変換
    const demandReservations = user.demandSchedules?.map((dc) => dc.reservations).flat() || [];
    const formatDemandReservations = demandReservations.map((rsv) => {
      const customerPickupTimes = rsv.demandCustomers?.map((dc) => dc.p_datetime) || [];
      const firstCustomerPickupTime = customerPickupTimes.reduce((acc, cur) => (acc > cur ? cur : acc), customerPickupTimes[0]);
      const targetSchedule = user.demandSchedules.find((ds) => ds.id === rsv.foreign_id);
      return {
        reservation_time: firstCustomerPickupTime,
        ...rsv,
        demandSchedule: targetSchedule,
      };
    });

    // [通常予約] order.reservationをreservationsの配列に変換
    const orderReservations = user.driverOrders.map((o) => ({ ...o.reservation, order: o }));
    const formatOrderReservations = orderReservations.map((rsv) => ({ ...rsv, reservation_time: rsv.datetime || rsv.created_at }));

    // デマンドと通常のreservationを合わせて時間順に並べ替え
    const driverReservations = [...formatDemandReservations, ...formatOrderReservations];
    const sortedReservations = driverReservations.sort((a, b) => (a.reservation_time < b.reservation_time ? 1 : -1));

    commit('setOrders', sortedReservations);
  },
};

const mutations = {
  setUserData(state, data) {
    const keys = Object.keys(data);
    keys.some((key) => {
      // passwordはstoreで保持しない
      if (key !== 'password') {
        state[key] = data[key];
      }
      return false;
    });
  },

  update(state, user) {
    const userKeys = Object.keys(user);
    const stateKeys = Object.keys(state);
    // 取得したuserの値をまわして
    userKeys.some((key) => {
      // キーが存在するstateを更新
      if (stateKeys.includes(key)) {
        // console.log(key);
        state[key] = user[key];
      }
      return false;
    });
  },

  setRequests(state, requests) {
    state.requests = requests;
  },
  setOrders(state, orders) {
    state.orders = orders;
  },
  setDriverStatus(state, data) {
    state.driverStatus = data;
  },

  setFlag(state, newFlag) {
    state.flag = newFlag;
  },
};

const state = {
  id: '',
  requests: [],
  orders: [],
  driverStatus: {
    orderStatus: null,
    isBreak: false,
    isWaiting: false,
    isClosed: false,
    isEmergency: false,
  },
};


export default {
  namespaced: true,
  actions,
  mutations,
  state,
  modules: {
    login,
  },
};
